// src/components/pages/dialogs/HolidayDialog.tsx
'use client';

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Calendar, Trash2, Copy, Plus, X, ChevronLeft } from 'lucide-react';
import { useFestivos } from '@/components/hooks/useFestivos';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import {
  DLG_BTN_PRIMARY,
  DLG_BTN_SECONDARY,
} from '@/components/ui/dialogButtons';

// Clases del bottom sheet (móvil) / diálogo centrado (PC)
const SHEET_CLASS = `
  z-[60] p-0 border border-gray-200 dark:border-white/10 bg-white dark:bg-[#1c1c1e]
  [&>button:last-child]:hidden
  flex flex-col
  !bottom-0 !left-0 !top-auto !translate-x-0 !translate-y-0
  w-full max-w-none max-h-[85vh] rounded-t-2xl rounded-b-none
  data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom
  sm:!bottom-auto sm:!left-1/2 sm:!top-1/2 sm:!-translate-x-1/2 sm:!-translate-y-1/2
  sm:w-[95%] sm:max-w-lg sm:mx-auto sm:h-auto sm:max-h-[85vh] sm:rounded-xl sm:shadow-xl
`;

type Vista = 'lista' | 'crear' | 'copiar';

interface Props {
  id_empresa: string;
  className?: string;
}

export default function HolidayDialog({ id_empresa, className }: Props) {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [vista, setVista] = useState<Vista>('lista');

  const userId = localStorage.getItem('id_usuario');

  const {
    festivos,
    loading,
    añoSeleccionado,
    cambiarAño,
    crearFestivo,
    eliminarFestivo,
    copiarFestivos,
    refrescar,
  } = useFestivos(id_empresa);

  // ── Estado del formulario "crear" ──
  const hoy = new Date().toISOString().slice(0, 10);
  const [fecha, setFecha] = useState(hoy);
  const [motivo, setMotivo] = useState('');

  // ── Estado del formulario "copiar" ──
  const [añoOrigen, setAñoOrigen] = useState(añoSeleccionado);
  const [añoDestino, setAñoDestino] = useState(añoSeleccionado + 1);

  const handleOpen = async () => {
    setOpen(true);
    setVista('lista');
    await refrescar();
  };

  // Al entrar en cada vista, reseteamos sus campos
  const irACrear = () => {
    setFecha(hoy);
    setMotivo('');
    setVista('crear');
  };
  const irACopiar = () => {
    setAñoOrigen(añoSeleccionado);
    setAñoDestino(añoSeleccionado + 1);
    setVista('copiar');
  };

  const añosDisponibles = useMemo(
    () => [
      añoSeleccionado - 2,
      añoSeleccionado - 1,
      añoSeleccionado,
      añoSeleccionado + 1,
      añoSeleccionado + 2,
    ],
    [añoSeleccionado]
  );

  const handleGuardarFestivo = async () => {
    const { ok } = await crearFestivo({ fecha, motivo, usuario: userId });
    if (ok) setVista('lista');
  };

  const handleCopiar = async () => {
    const { ok } = await copiarFestivos({ añoOrigen, añoDestino });
    if (!ok) return;
    await cambiarAño(añoDestino);
    setVista('lista');
  };

  const titulo =
    vista === 'crear'
      ? t('holidayDialog.createTitle')
      : vista === 'copiar'
      ? t('copyHolidaysDialog.title')
      : t('holidayDialog.title');

  return (
    <div className={className}>
      <Button
        onClick={handleOpen}
        disabled={loading}
        className="w-full rounded-lg text-white font-medium shadow-md hover:bg-[#0d2742] dark:hover:bg-[#0d2742] flex items-center justify-center gap-2 px-4 py-2 transition-colors"
        style={{ backgroundColor: '#103254' }}
      >
        <Calendar className="w-4 h-4 text-white" />
        {loading ? t('holidayDialog.btnLoading') : t('holidayDialog.btnOpen')}
      </Button>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) setVista('lista');
        }}
      >
        <DialogContent className={SHEET_CLASS}>
          {/* Header: título + (Volver si no es lista) + X */}
          <div className="px-4 sm:px-6 pt-5 pb-3 shrink-0 relative flex items-center gap-2">
            {vista !== 'lista' && (
              <button
                onClick={() => setVista('lista')}
                className="shrink-0 -ml-1 w-8 h-8 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                aria-label={t('newAppointmentDialog.back', 'Atrás')}
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
            )}
            <DialogHeader className="flex-1 min-w-0">
              <DialogTitle className="text-lg sm:text-xl font-semibold text-[#103254] dark:text-blue-400 pr-10 truncate">
                {titulo}
              </DialogTitle>
            </DialogHeader>
            <button
              onClick={() => setOpen(false)}
              className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full text-gray-500 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
              aria-label="Cerrar"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Contenido con scroll */}
          <div className="flex-1 overflow-y-auto px-4 sm:px-6 pb-5">
            {/* ══ VISTA LISTA ══ */}
            {vista === 'lista' && (
              <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-6 text-sm">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-2">
                    <Label className="font-medium text-gray-700 dark:text-gray-300">
                      {t('holidayDialog.labelYear')}
                    </Label>

                    <Select
                      value={String(añoSeleccionado)}
                      onValueChange={(v) => cambiarAño(Number(v))}
                    >
                      <SelectTrigger className="h-9 text-sm min-w-[80px] focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[70]">
                        {añosDisponibles.map((a) => (
                          <SelectItem
                            key={a}
                            value={String(a)}
                            className="text-sm"
                          >
                            {a}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      onClick={irACrear}
                      disabled={loading}
                      className="flex-1 sm:flex-none rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] transition-colors flex items-center justify-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      {t('holidayDialog.btnAdd')}
                    </Button>

                    <Button
                      type="button"
                      onClick={irACopiar}
                      disabled={loading}
                      className="flex-1 sm:flex-none rounded-lg text-white font-medium shadow-md bg-[#103254] hover:bg-[#0d2742] dark:bg-[#103254] dark:hover:bg-[#0d2742] transition-colors flex items-center justify-center gap-2"
                    >
                      <Copy className="w-4 h-4" />
                      {t('holidayDialog.btnCopy')}
                    </Button>
                  </div>
                </div>

                <hr className="border-t border-gray-200 dark:border-white/10" />

                {festivos.length > 0 ? (
                  <ul className="space-y-2 max-h-72 overflow-y-auto pr-1">
                    {festivos.map((f) => (
                      <li
                        key={f.id_festivo}
                        className="flex items-center justify-between bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/10 rounded-lg px-3 py-2 shadow-sm"
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-sm text-gray-900 dark:text-white">
                            {f.fecha}
                          </p>
                          {f.motivo && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 truncate">
                              {f.motivo}
                            </p>
                          )}
                        </div>

                        <Button
                          variant="ghost"
                          size="icon"
                          disabled={loading}
                          onClick={() => eliminarFestivo(f.id_festivo)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {t('holidayDialog.noHolidays', { year: añoSeleccionado })}
                  </p>
                )}
              </div>
            )}

            {/* ══ VISTA CREAR ══ */}
            {vista === 'crear' && (
              <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-6 text-sm">
                <div className="space-y-2">
                  <Label className="font-medium text-gray-700 dark:text-gray-300">
                    {t('holidayDialog.labelDate')}
                  </Label>
                  <Input
                    type="date"
                    value={fecha}
                    onChange={(e) => setFecha(e.target.value)}
                    className="h-10 rounded-md text-sm border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white min-w-0 appearance-none"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="font-medium text-gray-700 dark:text-gray-300">
                    {t('holidayDialog.labelReason')}
                  </Label>
                  <Textarea
                    placeholder={t('holidayDialog.reasonPlaceholder')}
                    value={motivo}
                    onChange={(e) => setMotivo(e.target.value)}
                    className="rounded-md text-sm resize-none border-gray-300 dark:border-white/10 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  />
                </div>
              </div>
            )}

            {/* ══ VISTA COPIAR ══ */}
            {vista === 'copiar' && (
              <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-white/5 shadow-sm p-4 space-y-6 text-sm">
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('copyHolidaysDialog.description')}
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="font-medium text-gray-700 dark:text-gray-300">
                      {t('copyHolidaysDialog.sourceYear')}
                    </Label>
                    <Select
                      value={String(añoOrigen)}
                      onValueChange={(v) => setAñoOrigen(Number(v))}
                    >
                      <SelectTrigger className="h-9 text-sm w-full focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[70]">
                        {añosDisponibles.map((a) => (
                          <SelectItem
                            key={a}
                            value={String(a)}
                            className="text-sm"
                          >
                            {a}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label className="font-medium text-gray-700 dark:text-gray-300">
                      {t('copyHolidaysDialog.targetYear')}
                    </Label>
                    <Select
                      value={String(añoDestino)}
                      onValueChange={(v) => setAñoDestino(Number(v))}
                    >
                      <SelectTrigger className="h-9 text-sm w-full focus:ring-[#103254]/30 dark:bg-white/5 dark:border-white/10 dark:text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[70]">
                        {añosDisponibles.map((a) => (
                          <SelectItem
                            key={a}
                            value={String(a)}
                            className="text-sm"
                          >
                            {a}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {añoOrigen === añoDestino && (
                  <p className="text-xs text-red-500">
                    {t('copyHolidaysDialog.sameYearError')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Footer con línea — solo en crear/copiar (Volver primero) */}
          {vista === 'crear' && (
            <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
              <Button
                type="button"
                variant="outline"
                onClick={() => setVista('lista')}
                disabled={loading}
                className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
              >
                {t('newAppointmentDialog.back', 'Volver')}
              </Button>
              <Button
                onClick={handleGuardarFestivo}
                disabled={loading}
                className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY}`}
              >
                {loading ? t('holidayDialog.saving') : t('holidayDialog.save')}
              </Button>
            </DialogFooter>
          )}

          {vista === 'copiar' && (
            <DialogFooter className="px-4 sm:px-6 pt-2 pb-5 sm:pb-4 flex flex-col sm:flex-row gap-3 shrink-0 border-t border-gray-100 dark:border-white/10">
              <Button
                type="button"
                variant="outline"
                onClick={() => setVista('lista')}
                disabled={loading}
                className={`w-full sm:flex-1 ${DLG_BTN_SECONDARY}`}
              >
                {t('newAppointmentDialog.back', 'Volver')}
              </Button>
              <Button
                onClick={handleCopiar}
                disabled={loading || añoOrigen === añoDestino}
                className={`w-full sm:flex-1 ${DLG_BTN_PRIMARY} flex items-center justify-center gap-2`}
              >
                <Copy className="w-4 h-4" />
                {loading
                  ? t('copyHolidaysDialog.copying')
                  : t('copyHolidaysDialog.copyBtn', {
                      origen: añoOrigen,
                      destino: añoDestino,
                    })}
              </Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}